import { toast } from '@postscript/components';
import { toastError } from 'components/billing/common/utils';
import { QueryCacheTimes } from 'components/flowBuilder/constants';
import { useUser } from 'controllers/contexts/user';
import { api } from 'controllers/network/apiClient';
import { SHOPPER_STATUS_QUERY_KEY } from 'providers/shopperStatusProvider';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  brandingKey,
  brandingStatusKey,
  fileSourcesKey,
  initAIGeneratedBrandingKey,
  inpirationMessagesKey,
  linkSourcesKey,
} from '../sources/constants';
import {
  AddLinkSourceParams,
  Branding,
  BrandingFileSource,
  BrandingLinkSource,
  BrandingStatus,
  BrandingType,
  BrandingValues,
  BrandVoice,
  GeneratedMessage,
  InspirationPayload,
  PersistRangeBoundaryParams,
  RangeAttributeBoundaries,
  UpdateFileSourceParams,
  UpdateLinkSourceParams,
} from '../types';
import { buildBrandingPayload, toCamelCase, toSnakeCase } from '../utils';
import { StartPollingFn } from './useActivePollingLinks';

export const BRAND_VOICE_QUERY_KEY = 'brand-voice';

const rangeBoundaries: RangeAttributeBoundaries = new Map();

export function usePersistRangeBoundary({
  name,
  min,
  max,
}: PersistRangeBoundaryParams): void {
  rangeBoundaries.set(name, [min, max]);
}

export const useGetBrandVoice = (): UseQueryResult<BrandVoice> => {
  return useQuery({
    queryKey: [BRAND_VOICE_QUERY_KEY],
    queryFn: async () => {
      return api.get('/v2/ai-components/brand-voice');
    },
  });
};

export const useGetBrandingStatus = (
  options?: UseQueryOptions<
    BrandingStatus,
    any,
    BrandingStatus,
    [typeof brandingStatusKey]
  >,
): UseQueryResult<BrandingStatus> => {
  return useQuery(
    [brandingStatusKey],
    async (): Promise<BrandingStatus> => {
      const brandingStatus = await api.get(
        '/v2/marketing-ai/brand-voices/status',
        {
          throwErrorAsString: false,
        },
      );
      return brandingStatus;
    },
    {
      // Since this is used on the actual home page I don't want to toast
      // errors ever.
      onError: async (error) => console.log(error),
      staleTime: QueryCacheTimes.SHORT,
      ...options,
    },
  );
};

export function useInitAIGeneratedBranding(): UseMutationResult<
  void,
  any,
  void
> {
  const queryClient = useQueryClient();
  const {
    user: { shop_id: shopId },
  } = useUser();

  return useMutation(
    async () => {
      queryClient.setQueryData(
        [brandingKey, 'ai_generated'],
        toCamelCase(
          await api.post('/v2/branding/ai_generated', {
            shop_id: shopId,
          }),
        ),
      );
    },
    { mutationKey: [initAIGeneratedBrandingKey] },
  );
}

export function useUpdateBranding(): UseMutationResult<
  Branding,
  any,
  { guid: string; values: BrandingValues }
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ guid, values }) => {
      const payload = buildBrandingPayload(values, rangeBoundaries);
      return api.post(`/v2/branding/${guid}/attributes`, toSnakeCase(payload));
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          [brandingKey, data.type.toLowerCase()],
          toCamelCase(data),
        );
        queryClient.invalidateQueries([SHOPPER_STATUS_QUERY_KEY]);
      },
      onError: toastError,
    },
  );
}

export const useInspirationMessages = (): UseMutationResult<
  GeneratedMessage[],
  any,
  { payload: InspirationPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ payload }) => {
      const { generated_messages: generatedMessages } = await api.post(
        '/v2/marketing-ai/brand-voices/preview-messages-mai',
        toSnakeCase(payload),
      );
      return toCamelCase(generatedMessages);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData([inpirationMessagesKey], data);
      },
      onError: () =>
        toast.error('Error retrieving example messages. Try again later.'),
    },
  );
};

export function useUploadFile(
  options = {
    maxImageSize: 50 * 1024 * 1024, // 50 MB
    maxImageSizeError: 'Must be smaller than 50 MB',
  },
): UseMutationResult<string, any, File> {
  return useMutation(async (file) => {
    const { maxImageSize, maxImageSizeError } = options;

    if (file.size > maxImageSize) {
      throw new Error(maxImageSizeError);
    }

    const formData = new FormData();
    formData.append('file', file);

    const { url } = await api.postFormData('/v2/branding/file', formData);
    return url;
  });
}

export function useCreateBrandingByType(): UseMutationResult<
  Branding,
  any,
  BrandingType
> {
  const queryClient = useQueryClient();
  const {
    user: { shop_id: shopId },
  } = useUser();

  return useMutation(
    async (type) => {
      return api.post('/v2/branding/', {
        shop_id: shopId,
        type,
      });
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          [brandingKey, data.type.toLowerCase()],
          toCamelCase(data),
        );
      },
      onError: () => toast.error('Something went wrong. Try again later.'),
    },
  );
}

export function useGetBrandingByType(
  type: BrandingType,
  options?: UseQueryOptions<
    Branding,
    any,
    Branding,
    [typeof brandingKey, BrandingType]
  >,
): UseQueryResult<Branding> {
  return useQuery(
    [brandingKey, type],
    async (): Promise<Branding> => {
      return toCamelCase(await api.get(`/v2/branding/${type}`));
    },
    {
      staleTime: QueryCacheTimes.SHORT,
      ...options,
    },
  );
}

export function useGenerateFileAssistedAttributes(): UseMutationResult<
  Branding,
  any,
  { guid: string }
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ guid }) => {
      return api.post(`/v2/branding/${guid}/attributes/file_assisted`);
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          [brandingKey, data.type.toLowerCase()],
          toCamelCase(data),
        );
      },
      onError: toastError,
    },
  );
}

export function useGetBrandingFileSources(
  options?: UseQueryOptions<
    BrandingFileSource[],
    any,
    BrandingFileSource[],
    [typeof fileSourcesKey]
  >,
): UseQueryResult<BrandingFileSource[]> {
  return useQuery(
    [fileSourcesKey],
    async (): Promise<BrandingFileSource[]> => {
      const { files } = await api.get('/v2/branding/sources/files');
      return files;
    },
    {
      staleTime: QueryCacheTimes.SHORT,
      ...options,
    },
  );
}

interface UploadFileSourceParams {
  file: File;
  category: string;
  active: boolean;
}

export function useUploadFileSource(
  options = {
    maxImageSize: 50 * 1024 * 1024, // 50 MB
    maxImageSizeError: 'Must be smaller than 50 MB',
  },
): UseMutationResult<BrandingFileSource, any, UploadFileSourceParams> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      file,
      category,
      active,
    }: UploadFileSourceParams): Promise<BrandingFileSource> => {
      const { maxImageSize, maxImageSizeError } = options;

      if (file.size > maxImageSize) {
        throw new Error(maxImageSizeError);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('category', category);
      formData.append('active', active.toString());

      return api.postFormData('/v2/branding/sources/files', formData);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([fileSourcesKey]),
      onError: toastError,
    },
  );
}

export function useUpdateFileSource(): UseMutationResult<
  BrandingFileSource,
  any,
  UpdateFileSourceParams
> {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      name,
      category,
      active,
    }: UpdateFileSourceParams): Promise<BrandingFileSource> => {
      return api.put(`/v2/branding/sources/files/${id}`, {
        name,
        category,
        active,
      });
    },
    {
      onSuccess: () => queryClient.invalidateQueries([fileSourcesKey]),
      onError: toastError,
    },
  );
}

export function useDeleteFileSource(): UseMutationResult<void, any, number> {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number): Promise<void> => {
      return api.delete(`/v2/branding/sources/files/${id}`);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([fileSourcesKey]),
      onError: toastError,
    },
  );
}

export function useGetBrandingLinkSources(
  options?: UseQueryOptions<
    BrandingLinkSource[],
    any,
    BrandingLinkSource[],
    [typeof linkSourcesKey]
  >,
): UseQueryResult<BrandingLinkSource[]> {
  return useQuery(
    [linkSourcesKey],
    async (): Promise<BrandingLinkSource[]> => {
      const { links } = await api.get('/v2/branding/sources/links');
      return links;
    },
    {
      staleTime: QueryCacheTimes.SHORT,
      ...options,
    },
  );
}

export function useAddLinkSource(startPolling: StartPollingFn) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      url,
      category,
      active,
    }: AddLinkSourceParams): Promise<BrandingLinkSource> => {
      return api.post('/v2/branding/sources/links', { url, category, active });
    },
    onSuccess: (link) => {
      queryClient.invalidateQueries([linkSourcesKey]);
      startPolling(link);
    },
    onError: toastError,
  });
}

export function useUpdateLinkSource(startPolling: StartPollingFn) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      url,
      category,
      active,
    }: UpdateLinkSourceParams): Promise<BrandingLinkSource> => {
      return api.put(`/v2/branding/sources/links/${id}`, {
        url,
        category,
        active,
      });
    },
    // Optimistically update the cache before the mutation
    onMutate: async (variables) => {
      await queryClient.cancelQueries([linkSourcesKey]);

      const previousLinks = queryClient.getQueryData<BrandingLinkSource[]>([
        linkSourcesKey,
      ]);

      queryClient.setQueryData<BrandingLinkSource[]>(
        [linkSourcesKey],
        (old) =>
          old?.map((link) =>
            link.id === variables.id
              ? { ...link, active: variables.active }
              : link,
          ) ?? [],
      );

      return { previousLinks };
    },

    // If mutation fails, rollback
    onError: (_err, _vars, context) => {
      if (context?.previousLinks) {
        queryClient.setQueryData([linkSourcesKey], context.previousLinks);
      }
      toast.error('Failed to update link.');
    },

    // On success, begin polling the link for async data changes
    onSuccess: (updatedLink) => {
      startPolling(updatedLink);
    },
  });
}

export function useDeleteLinkSource(): UseMutationResult<void, any, number> {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number): Promise<void> => {
      return api.delete(`/v2/branding/sources/links/${id}`);
    },
    {
      onSuccess: () => queryClient.invalidateQueries([linkSourcesKey]),
      onError: toastError,
    },
  );
}

export function useInitLinkScraping(): UseMutationResult<void, any, void> {
  return useMutation(
    async (): Promise<void> => {
      return api.post('/v2/branding/sources/links/scrape');
    },
    {
      onError: toastError,
    },
  );
}
